import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Container, Box, Typography, Paper, IconButton, Chip, alpha, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, subMonths, addMonths, eachDayOfInterval, isToday, isPast, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { getBrusselsDate } from '../utils/dateUtils';

const statusConfig = {
  0: { label: 'Indisponible', color: 'error.main', bgColor: alpha('#f44336', 0.2) },
  1: { label: 'Disponible', color: 'success.main', bgColor: alpha('#4caf50', 0.2) },
  2: { label: 'À confirmer', color: 'warning.main', bgColor: alpha('#ff9800', 0.2) },
};

const TeamAvailability = () => {
  const [currentMonth, setCurrentMonth] = useState(getBrusselsDate());
  const [allAvailabilities, setAllAvailabilities] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { activeCompany } = useContext(AuthContext);

  // Fetch users only once or when company changes
  useEffect(() => {
    const fetchUsers = async () => {
      if (!activeCompany) return;
      try {
        const usersRes = await axiosInstance.get(`/company/${activeCompany.id}/users?status=active`);
        const sortedUsers = usersRes.data.sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''));
        setUsers(sortedUsers);
        if (sortedUsers.length > 0) {
          setSelectedUserId(sortedUsers[0].id);
        }
      } catch (err) {
        setError('Erreur lors du chargement de la liste des employés.');
        console.error(err);
      }
    };
    fetchUsers();
  }, [activeCompany]);

  // Fetch availabilities when month or company changes
  useEffect(() => {
    const fetchAvailabilities = async () => {
    if (!activeCompany) return;
    setLoading(true);
    try {
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      const availabilitiesRes = await axiosInstance.get(`/availability/company/${activeCompany.id}?startDate=${format(calendarStart, 'yyyy-MM-dd')}&endDate=${format(calendarEnd, 'yyyy-MM-dd')}`);
      setAllAvailabilities(availabilitiesRes.data);
    } catch (err) {
      setError('Erreur lors du chargement des disponibilités de l\'équipe.');
      console.error(err);
    } finally {
      setLoading(false);
    }
    };
    fetchAvailabilities();
  }, [activeCompany, currentMonth]);

  const handleUserChange = (event) => {
    setSelectedUserId(event.target.value);
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    const weekDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

    const userAvailabilities = allAvailabilities.filter(a => a.userId === selectedUserId);

    return (
      <Box sx={{ overflowX: 'auto' }}>
        <Paper sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          border: '1px solid',
          borderColor: 'divider',
          userSelect: 'none',
        }}>
          {weekDays.map(day => (
            <Box key={day} sx={{ textAlign: 'center', p: 1, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.100' }}>
              <Typography variant="subtitle2" sx={{ display: { xs: 'none', sm: 'block' } }}>{day}</Typography>
              <Typography variant="subtitle2" sx={{ display: { xs: 'block', sm: 'none' } }}>{day.substring(0, 3)}</Typography>
            </Box>
          ))}

          {days.map((day, index) => {
            const dateString = format(day, 'yyyy-MM-dd');
            const availabilitiesForDay = userAvailabilities
              .filter(a => a.date === dateString)
              .sort((a, b) => (a.startTime || '00:00').localeCompare(b.startTime || '00:00'));

            const allDayAvailability = availabilitiesForDay.find(av => av.startTime === null);
            const timedAvailabilities = availabilitiesForDay.filter(av => av.startTime !== null);

            const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
            const isCurrentDay = isToday(day);
            const isDayInThePast = isPast(endOfDay(day));

            return (
              <Box
                key={dateString}
                sx={{
                  borderTop: '1px solid',
                  borderRight: (index + 1) % 7 !== 0 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  minHeight: 70,
                  p: 0.5,
                  opacity: isDayInThePast ? 0.6 : (isCurrentMonth ? 1 : 0.6),
                  backgroundColor: allDayAvailability ? statusConfig[allDayAvailability.status].bgColor : 'transparent',
                  boxShadow: isCurrentDay ? `inset 0 0 0 3px #4caf50` : 'none',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {format(day, 'd')}
                </Typography>
                {timedAvailabilities.map(av => {
                  const style = statusConfig[av.status];
                  return (
                    <Chip
                      key={av.id}
                      label={
                        <Box sx={{ display: { xs: 'block' }, textAlign: 'center' }}>
                          <Typography variant="caption" fontWeight='bold' component="div" lineHeight={1.1} sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                            {av.startTime.substring(0, 5)} <br /> {av.endTime.substring(0, 5)}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        backgroundColor: style.bgColor, color: style.color, fontWeight: 'bold',
                        width: '100%', justifyContent: 'center', mb: 0.5, borderRadius: 1,
                        '& .MuiChip-label': { padding: '0px' },
                      }}
                      size="medium" />
                  );
                })}
              </Box>
            );
          })}
        </Paper>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl fullWidth>
          <InputLabel id="employee-select-label">Employé</InputLabel>
          <Select
            labelId="employee-select-label"
            value={selectedUserId}
            label="Employé"
            onChange={handleUserChange}
            disabled={loading || users.length === 0}
          >
            {users.map(user => (
              <MenuItem key={user.id} value={user.id}>
                {user.firstName} {user.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Paper sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, mb: 2 }}>
        <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} disabled={loading}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h5" sx={{ mx: 3, textTransform: 'capitalize' }}>
          {format(currentMonth, 'MMMM yyyy', { locale: fr })}
        </Typography>
        <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} disabled={loading}>
          <ChevronRightIcon />
        </IconButton>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : selectedUserId ? (
        renderCalendar()
      ) : (
        <Typography>Veuillez sélectionner un employé pour voir ses disponibilités.</Typography>
      )}
    </Container>
  );
};

export default TeamAvailability;
